/**
 * TenantConnect — invite-code entry page for authenticated tenants.
 *
 * Flow:
 *   1. Tenant is already logged in (role = tenant) but tenant_links.tenant_user_id
 *      is not yet set for them.
 *   2. Tenant enters the 6-char invite code their landlord gave them.
 *   3. We call claim_tenant_invite RPC which atomically links the account
 *      and sets profiles.role = 'tenant'.
 *   4. Session is refreshed so AppContext picks up the updated role.
 *   5. Success screen shows the linked property name, then redirects to dashboard.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { KeyRound, CheckCircle2, Home, AlertCircle, Loader2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';

type Stage = 'idle' | 'loading' | 'success' | 'error';

export default function TenantConnect() {
    const navigate = useNavigate();

    const [code, setCode] = useState('');
    const [stage, setStage] = useState<Stage>('idle');
    const [errorMsg, setErrorMsg] = useState('');
    const [linkedProperty, setLinkedProperty] = useState<{ name: string; address: string } | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!code.trim()) return;

        setStage('loading');
        setErrorMsg('');

        try {
            const { data: rpcResult, error: rpcError } = await supabase.rpc(
                'claim_tenant_invite',
                { invite_code_input: code.trim() }
            );

            if (rpcError) {
                setErrorMsg('Something went wrong. Please try again.');
                setStage('error');
                return;
            }

            if (!rpcResult.success) {
                if (rpcResult.already_linked) {
                    // Already linked to this account — just go to dashboard
                    navigate('/app/tenant/dashboard', { replace: true });
                    return;
                }
                setErrorMsg(rpcResult.error || 'Could not link your account. Please try again.');
                setStage('error');
                return;
            }

            // Refresh session so AppContext re-reads the updated profiles.role
            await supabase.auth.refreshSession();

            // Show linked property info if returned by RPC
            if (rpcResult.property_name) {
                setLinkedProperty({
                    name: rpcResult.property_name,
                    address: rpcResult.property_address || '',
                });
            }

            setStage('success');

            // Auto-redirect after 1.8 s (just long enough to read the success screen)
            setTimeout(() => navigate('/app/tenant/dashboard', { replace: true }), 1800);

        } catch (err) {
            console.error('[TENANT CONNECT]', err);
            setErrorMsg('Something went wrong. Please try again.');
            setStage('error');
        }
    };

    /* ── Success screen ── */
    if (stage === 'success') {
        return (
            <div className="page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '70vh' }}>
                <div style={{
                    textAlign: 'center',
                    padding: '3rem 2rem',
                    maxWidth: 440,
                    animation: 'slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
                }}>
                    <div style={{
                        width: 72, height: 72,
                        borderRadius: '50%',
                        background: 'rgba(34, 197, 94, 0.1)',
                        border: '2px solid rgba(34, 197, 94, 0.3)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        margin: '0 auto 1.5rem',
                    }}>
                        <CheckCircle2 size={36} color="#16a34a" />
                    </div>

                    <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.625rem', fontWeight: 800, color: 'var(--color-text)' }}>
                        You're connected!
                    </h2>

                    {linkedProperty ? (
                        <>
                            <p style={{ margin: '0 0 1rem', color: 'var(--color-text-secondary)', fontSize: '1rem' }}>
                                Your account is now linked to:
                            </p>
                            <div style={{
                                background: 'var(--color-bg-secondary)',
                                border: '1px solid var(--color-border)',
                                borderRadius: '12px',
                                padding: '1.25rem 1.5rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '1rem',
                                marginBottom: '2rem',
                                textAlign: 'left',
                            }}>
                                <div style={{
                                    width: 44, height: 44, borderRadius: '10px',
                                    background: 'rgba(99, 102, 241, 0.1)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    flexShrink: 0,
                                }}>
                                    <Home size={22} color="var(--color-primary)" />
                                </div>
                                <div>
                                    <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--color-text)' }}>
                                        {linkedProperty.name}
                                    </div>
                                    {linkedProperty.address && (
                                        <div style={{ fontSize: '0.875rem', color: 'var(--color-text-tertiary)', marginTop: '2px' }}>
                                            {linkedProperty.address}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    ) : (
                        <p style={{ margin: '0 0 2rem', color: 'var(--color-text-secondary)' }}>
                            Your account has been linked. Taking you to your dashboard…
                        </p>
                    )}

                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', color: 'var(--color-text-tertiary)', fontSize: '0.875rem' }}>
                        <Loader2 size={16} className="animate-spin" />
                        Redirecting to your dashboard…
                    </div>
                </div>
                <style>{`@keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }`}</style>
            </div>
        );
    }

    /* ── Main form ── */
    return (
        <div className="page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '70vh' }}>
            <div style={{ width: '100%', maxWidth: 440, padding: '0 1rem' }}>

                {/* Icon */}
                <div style={{
                    width: 64, height: 64, borderRadius: '16px',
                    background: 'rgba(99, 102, 241, 0.1)',
                    border: '1.5px solid rgba(99, 102, 241, 0.2)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    margin: '0 auto 1.75rem',
                }}>
                    <KeyRound size={28} color="var(--color-primary)" />
                </div>

                <h1 style={{ fontSize: '1.75rem', fontWeight: 800, textAlign: 'center', margin: '0 0 0.5rem', color: 'var(--color-text)' }}>
                    Connect your home
                </h1>
                <p style={{ textAlign: 'center', color: 'var(--color-text-secondary)', margin: '0 0 2.5rem', lineHeight: 1.6 }}>
                    Enter the invite code your landlord sent you to link your account to your property.
                </p>

                {/* Error banner */}
                {stage === 'error' && (
                    <div style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '0.75rem',
                        background: '#fef2f2',
                        border: '1px solid #fecaca',
                        borderRadius: '10px',
                        padding: '0.875rem 1rem',
                        marginBottom: '1.5rem',
                    }}>
                        <AlertCircle size={18} color="#dc2626" style={{ flexShrink: 0, marginTop: '1px' }} />
                        <p style={{ margin: 0, fontSize: '0.875rem', color: '#b91c1c', lineHeight: 1.5 }}>
                            {errorMsg}
                        </p>
                    </div>
                )}

                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <div>
                        <label style={{
                            display: 'block',
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            color: 'var(--color-text)',
                            marginBottom: '0.5rem',
                        }}>
                            Invite Code
                        </label>
                        <input
                            type="text"
                            value={code}
                            onChange={(e) => {
                                setCode(e.target.value.toUpperCase());
                                if (stage === 'error') setStage('idle');
                            }}
                            placeholder="e.g. ABC123"
                            maxLength={10}
                            autoFocus
                            autoComplete="off"
                            style={{
                                width: '100%',
                                padding: '0.875rem 1rem',
                                border: `2px solid ${stage === 'error' ? '#fca5a5' : code ? 'var(--color-primary)' : 'var(--color-border)'}`,
                                borderRadius: '10px',
                                fontSize: '1.375rem',
                                fontFamily: 'monospace',
                                fontWeight: 700,
                                letterSpacing: '0.2em',
                                textAlign: 'center',
                                textTransform: 'uppercase',
                                outline: 'none',
                                transition: 'border-color 0.2s',
                                boxSizing: 'border-box',
                                background: '#fff',
                            }}
                        />
                        <p style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)', marginTop: '0.375rem', textAlign: 'center' }}>
                            Case-insensitive · usually 6 characters
                        </p>
                    </div>

                    <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={stage === 'loading' || !code.trim()}
                        style={{ width: '100%', height: '48px', fontSize: '1rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                    >
                        {stage === 'loading' ? (
                            <><Loader2 size={18} className="animate-spin" /> Verifying…</>
                        ) : (
                            'Connect My Home'
                        )}
                    </button>
                </form>

                <p style={{ textAlign: 'center', fontSize: '0.8125rem', color: 'var(--color-text-tertiary)', marginTop: '1.5rem', lineHeight: 1.6 }}>
                    Don't have a code yet? Ask your landlord to send you one from their Tenants page.
                </p>
            </div>
        </div>
    );
}
